import {FC, useEffect, useMemo, useState} from 'react'
import Swal from 'sweetalert2'
import {KTIcon} from '../../../_metronic/helpers'
import {FranchiseProduct} from '../core/_models'
import {getFranchiseProducts, syncFranchiseProducts} from '../core/_request'

type Props = {
  franchiseId: number
}

type LinkFilter = 'all' | 'linked' | 'unlinked'

const normalize = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

export const FranchiseProducts: FC<Props> = ({franchiseId}) => {
  const [products, setProducts] = useState<FranchiseProduct[]>([])
  const [original, setOriginal] = useState<FranchiseProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [linkFilter, setLinkFilter] = useState<LinkFilter>('all')

  const load = async () => {
    setLoading(true)
    try {
      const data = await getFranchiseProducts(franchiseId)
      setProducts(data)
      setOriginal(data)
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error?.response?.data?.message ?? 'Erro ao carregar os produtos da franquia',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [franchiseId])

  const categories = useMemo(
    () =>
      Array.from(
        new Set(products.map((product) => product.category).filter(Boolean))
      ).sort() as string[],
    [products]
  )

  const visible = useMemo(() => {
    const term = normalize(search)
    return products.filter(
      (product) =>
        normalize(product.name).includes(term) &&
        (!category || product.category === category) &&
        (linkFilter === 'all' || (linkFilter === 'linked') === product.linked)
    )
  }, [products, search, category, linkFilter])

  const linkedCount = products.filter((product) => product.linked).length
  const allVisibleLinked = visible.length > 0 && visible.every((product) => product.linked)

  const update = (id: number, changes: Partial<FranchiseProduct>) =>
    setProducts((current) =>
      current.map((product) => (product.id === id ? {...product, ...changes} : product))
    )

  const toggleAllVisible = () => {
    const ids = new Set(visible.map((product) => product.id))
    setProducts((current) =>
      current.map((product) =>
        ids.has(product.id) ? {...product, linked: !allVisibleLinked} : product
      )
    )
  }

  const save = async () => {
    const linked = products.filter((product) => product.linked)
    const wasLinked = new Set(
      original.filter((product) => product.linked).map((product) => product.id)
    )
    const added = linked.filter((product) => !wasLinked.has(product.id)).length
    const removed = original.filter(
      (product) => product.linked && !linked.some((item) => item.id === product.id)
    ).length

    const result = await Swal.fire({
      title: 'Deseja salvar os produtos da franquia?',
      html:
        `${linked.length} produto(s) vinculado(s).` +
        (added
          ? `<br/>${added} produto(s) novo(s): poderão ser vendidos, mas só agendados após incluí-los em uma sala.`
          : '') +
        (removed ? `<br/>${removed} produto(s) removido(s), inclusive das salas da franquia.` : ''),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger',
        cancelButton: 'btn btn-secondary',
      },
    })
    if (!result.isConfirmed) return

    setSaving(true)
    try {
      await syncFranchiseProducts(franchiseId, {
        products: linked.map((product) => ({
          id: product.id,
          activeSale: product.activeSale,
          activeSchedule: product.activeSchedule,
          discount: product.discount,
        })),
      })
      await Swal.fire({
        position: 'center',
        icon: 'success',
        title: 'Salvo com sucesso',
        showConfirmButton: false,
        timer: 1500,
      })
      await load()
    } catch (error: any) {
      let text = error?.response?.data?.message
      if (error?.response?.status === 422) {
        text = Object.values(error.response.data.errors ?? {})
          .map((messages: any) => messages[0])
          .join('<br/>')
      }
      await Swal.fire({icon: 'error', title: 'Oops...', html: text})
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title flex-column'>
          <h3 className='fw-bold mb-1'>Produtos</h3>
          <span className='text-muted fs-7'>
            {linkedCount} de {products.length} produtos vinculados à franquia. As salas de cada
            produto são definidas no cadastro de salas.
          </span>
        </div>
        <div className='card-toolbar gap-3'>
          <div className='position-relative'>
            <KTIcon
              iconName='magnifier'
              className='fs-3 position-absolute ms-4 top-50 translate-middle-y'
            />
            <input
              type='text'
              className='form-control form-control-solid w-225px ps-12'
              placeholder='Pesquisar produto'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className='form-select form-select-solid w-175px'
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value=''>Todas as categorias</option>
            {categories.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <select
            className='form-select form-select-solid w-175px'
            value={linkFilter}
            onChange={(e) => setLinkFilter(e.target.value as LinkFilter)}
          >
            <option value='all'>Todos</option>
            <option value='linked'>Vinculados</option>
            <option value='unlinked'>Não vinculados</option>
          </select>
        </div>
      </div>

      <div className='card-body py-4'>
        {loading ? (
          <div className='d-flex justify-content-center py-10'>
            <span className='spinner-border text-danger' />
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-3'>
              <thead>
                <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                  <th className='w-25px'>
                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        title='Marcar/desmarcar os produtos listados'
                        checked={allVisibleLinked}
                        disabled={!visible.length}
                        onChange={toggleAllVisible}
                      />
                    </div>
                  </th>
                  <th className='min-w-200px'>Produto</th>
                  <th className='min-w-125px'>Categoria</th>
                  <th className='text-center min-w-75px'>Venda</th>
                  <th className='text-center min-w-75px'>Agenda</th>
                  <th className='min-w-125px'>Desconto máx. (%)</th>
                </tr>
              </thead>
              <tbody className='text-gray-700 fw-semibold'>
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={6} className='text-center text-muted py-8'>
                      Nenhum produto encontrado
                    </td>
                  </tr>
                )}
                {visible.map((product) => (
                  <tr key={product.id}>
                    <td>
                      <div className='form-check form-check-sm form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          checked={product.linked}
                          onChange={() => update(product.id, {linked: !product.linked})}
                        />
                      </div>
                    </td>
                    <td
                      role='button'
                      className={product.linked ? 'text-gray-900 fw-bold' : 'text-muted'}
                      onClick={() => update(product.id, {linked: !product.linked})}
                    >
                      {product.name}
                    </td>
                    <td className='text-muted'>{product.category}</td>
                    <td>
                      <div className='form-check form-switch form-check-custom form-check-solid justify-content-center'>
                        <input
                          className='form-check-input h-20px w-35px'
                          type='checkbox'
                          disabled={!product.linked}
                          checked={product.activeSale}
                          onChange={(e) => update(product.id, {activeSale: e.target.checked})}
                        />
                      </div>
                    </td>
                    <td>
                      <div className='form-check form-switch form-check-custom form-check-solid justify-content-center'>
                        <input
                          className='form-check-input h-20px w-35px'
                          type='checkbox'
                          disabled={!product.linked}
                          checked={product.activeSchedule}
                          onChange={(e) => update(product.id, {activeSchedule: e.target.checked})}
                        />
                      </div>
                    </td>
                    <td>
                      <input
                        type='number'
                        min={0}
                        max={95}
                        className='form-control form-control-sm form-control-solid w-100px'
                        disabled={!product.linked}
                        value={product.discount}
                        onChange={(e) =>
                          update(product.id, {
                            discount: Math.min(95, Math.max(0, Number(e.target.value) || 0)),
                          })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className='card-footer d-flex justify-content-end py-6 px-9'>
        <button
          type='button'
          className='btn btn-danger'
          disabled={loading || saving}
          onClick={save}
        >
          {!saving && 'Salvar produtos'}
          {saving && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Salvando...{' '}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </div>
  )
}
